import React, { useId } from 'react';

/**
 * Dual-tone service icons — navy fill + cyan accent.
 * Designed for dark/gradient icon plates.
 */
const svgProps = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 48 48',
  fill: 'none',
  'aria-hidden': true,
});

export const IconEnterprise = ({ size = 30 }) => {
  const gid = useId().replace(/:/g, '');
  return (
    <svg {...svgProps(size)}>
      <defs>
        <linearGradient id={gid} x1="8" y1="6" x2="40" y2="42" gradientUnits="userSpaceOnUse">
          <stop stopColor="#7DD3F0" />
          <stop offset="1" stopColor="#2EB5E8" />
        </linearGradient>
      </defs>
      <rect x="6" y="18" width="14" height="22" rx="2.5" fill={`url(#${gid})`} opacity="0.35" />
      <rect x="6" y="18" width="14" height="22" rx="2.5" stroke={`url(#${gid})`} strokeWidth="2" />
      <rect x="24" y="8" width="18" height="32" rx="2.5" fill={`url(#${gid})`} opacity="0.55" />
      <rect x="24" y="8" width="18" height="32" rx="2.5" stroke="#E8F7FC" strokeWidth="1.5" />
      <path d="M10 24h6M10 29h6M10 34h6" stroke="#E8F7FC" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M29 14h8M29 19h8M29 24h8M29 29h8" stroke="#001B3A" strokeWidth="1.8" strokeLinecap="round" opacity="0.45" />
    </svg>
  );
};

export const IconWeb = ({ size = 30 }) => {
  const gid = useId().replace(/:/g, '');
  return (
    <svg {...svgProps(size)}>
      <defs>
        <linearGradient id={gid} x1="6" y1="8" x2="42" y2="38" gradientUnits="userSpaceOnUse">
          <stop stopColor="#A8E4F7" />
          <stop offset="1" stopColor="#2EB5E8" />
        </linearGradient>
      </defs>
      <rect x="5" y="9" width="38" height="26" rx="4" fill={`url(#${gid})`} opacity="0.28" />
      <rect x="5" y="9" width="38" height="26" rx="4" stroke={`url(#${gid})`} strokeWidth="2" />
      <path d="M5 16.5h38" stroke={`url(#${gid})`} strokeWidth="2" />
      <circle cx="10" cy="12.75" r="1.3" fill="#E8F7FC" />
      <circle cx="14.5" cy="12.75" r="1.3" fill="#E8F7FC" opacity="0.7" />
      <circle cx="19" cy="12.75" r="1.3" fill="#E8F7FC" opacity="0.45" />
      <rect x="11" y="21" width="18" height="8" rx="1.5" fill="#E8F7FC" opacity="0.85" />
      <path d="M33 22h5M33 26h5" stroke="#E8F7FC" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M18 39h12" stroke={`url(#${gid})`} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M24 35v4" stroke={`url(#${gid})`} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
};

export const IconMobile = ({ size = 30 }) => {
  const gid = useId().replace(/:/g, '');
  return (
    <svg {...svgProps(size)}>
      <defs>
        <linearGradient id={gid} x1="14" y1="4" x2="34" y2="44" gradientUnits="userSpaceOnUse">
          <stop stopColor="#A8E4F7" />
          <stop offset="1" stopColor="#0A7AAD" />
        </linearGradient>
      </defs>
      <rect x="14" y="4" width="20" height="40" rx="4.5" fill={`url(#${gid})`} opacity="0.3" />
      <rect x="14" y="4" width="20" height="40" rx="4.5" stroke={`url(#${gid})`} strokeWidth="2" />
      <rect x="17.5" y="9" width="13" height="24" rx="1.5" fill="#E8F7FC" opacity="0.9" />
      <circle cx="24" cy="39" r="2" fill="#E8F7FC" />
      <path d="M21 7h6" stroke="#E8F7FC" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
};

export const IconCloud = ({ size = 30 }) => {
  const gid = useId().replace(/:/g, '');
  return (
    <svg {...svgProps(size)}>
      <defs>
        <linearGradient id={gid} x1="6" y1="14" x2="42" y2="36" gradientUnits="userSpaceOnUse">
          <stop stopColor="#C5EEF9" />
          <stop offset="1" stopColor="#2EB5E8" />
        </linearGradient>
      </defs>
      <path
        d="M14.5 34h19.2a7.3 7.3 0 0 0 .8-14.55 9.8 9.8 0 0 0-18.9 3.2A6.4 6.4 0 0 0 14.5 34z"
        fill={`url(#${gid})`}
        opacity="0.4"
      />
      <path
        d="M14.5 34h19.2a7.3 7.3 0 0 0 .8-14.55 9.8 9.8 0 0 0-18.9 3.2A6.4 6.4 0 0 0 14.5 34z"
        stroke={`url(#${gid})`}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M24 20v10" stroke="#E8F7FC" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M20.5 23.5 24 20l3.5 3.5" stroke="#E8F7FC" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="33" cy="18" r="2.2" fill="#E8F7FC" opacity="0.85" />
    </svg>
  );
};

export const IconDesign = ({ size = 30 }) => {
  const gid = useId().replace(/:/g, '');
  return (
    <svg {...svgProps(size)}>
      <defs>
        <linearGradient id={gid} x1="8" y1="8" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#A8E4F7" />
          <stop offset="1" stopColor="#0A7AAD" />
        </linearGradient>
      </defs>
      <circle cx="24" cy="24" r="16" fill={`url(#${gid})`} opacity="0.2" />
      <circle cx="24" cy="24" r="16" stroke={`url(#${gid})`} strokeWidth="2" />
      <circle cx="24" cy="24" r="6.5" fill="#E8F7FC" opacity="0.95" />
      <circle cx="24" cy="24" r="2.4" fill="#0A7AAD" />
      <path d="M24 8v5.5M24 34.5V40M8 24h5.5M34.5 24H40" stroke={`url(#${gid})`} strokeWidth="2" strokeLinecap="round" />
      <path d="M34.2 13.8 30.5 17.5" stroke="#E8F7FC" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
};

export const IconApi = ({ size = 30 }) => {
  const gid = useId().replace(/:/g, '');
  return (
    <svg {...svgProps(size)}>
      <defs>
        <linearGradient id={gid} x1="6" y1="10" x2="42" y2="38" gradientUnits="userSpaceOnUse">
          <stop stopColor="#C5EEF9" />
          <stop offset="1" stopColor="#2EB5E8" />
        </linearGradient>
      </defs>
      <path
        d="M18 12 8 24l10 12"
        stroke={`url(#${gid})`}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M30 12l10 12-10 12"
        stroke={`url(#${gid})`}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M22 16l4 16" stroke="#E8F7FC" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="24" cy="24" r="3.2" fill="#E8F7FC" opacity="0.9" />
      <circle cx="24" cy="24" r="1.4" fill="#0A7AAD" />
    </svg>
  );
};

/* Shared thinner icons for Home industries / benefits / engagement */
const lineBase = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

export const IconShield = () => (
  <svg {...lineBase}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

export const IconRetail = () => (
  <svg {...lineBase}>
    <path d="M3 9h18l-1.5 12h-15z" />
    <path d="M8 9V6a4 4 0 0 1 8 0v3" />
  </svg>
);

export const IconHealth = () => (
  <svg {...lineBase}>
    <path d="M20.8 6.6a5 5 0 0 0-8.8-2.2A5 5 0 0 0 3.2 6.6c0 5 8.8 11 8.8 11s8.8-6 8.8-11z" />
  </svg>
);

export const IconLogistics = () => (
  <svg {...lineBase}>
    <rect x="1" y="7" width="13" height="10" rx="1" />
    <path d="M14 10h4l3 3v4h-7z" />
    <circle cx="6" cy="19" r="1.6" />
    <circle cx="17.5" cy="19" r="1.6" />
  </svg>
);

export const IconFinance = () => (
  <svg {...lineBase}>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <line x1="6" y1="12" x2="6.01" y2="12" />
    <line x1="18" y1="12" x2="18.01" y2="12" />
  </svg>
);

export const IconRealEstate = () => (
  <svg {...lineBase}>
    <path d="M3 11l9-7 9 7" />
    <path d="M5 10v10h14V10" />
    <rect x="10" y="14" width="4" height="6" />
  </svg>
);

export const IconEducation = () => (
  <svg {...lineBase}>
    <path d="M22 9l-10-5L2 9l10 5z" />
    <path d="M6 11.5V17c0 1.5 2.7 3 6 3s6-1.5 6-3v-5.5" />
  </svg>
);

export const IconUsers = () => (
  <svg {...lineBase}>
    <path d="M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="3.5" />
    <path d="M22 20v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.5a4 4 0 0 1 0 7" />
  </svg>
);

export const IconTarget = () => (
  <svg {...lineBase}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.4" />
  </svg>
);

export const IconScale = () => (
  <svg {...lineBase}>
    <polyline points="3 17 9 11 13 15 21 7" />
    <polyline points="15 7 21 7 21 13" />
  </svg>
);

export const IconAi = () => (
  <svg {...lineBase}>
    <rect x="6" y="6" width="12" height="12" rx="2" />
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
  </svg>
);
